<template>
  <div class="pb-page wv">
    <PageHeader title="Widgets" :subtitle="'Embed published testimonials of ' + (brand ? brand.name : 'your brand') + ' on any website. New ones appear automatically.'">
      <!-- Not while the list is empty: the empty state's "Create widget" is the one way in then. -->
      <template v-if="!loading && widgets.length">
        <LockedAction v-if="newCap" feature="widgets" look="pb-button" icon="mdi-plus" label="New widget" :cap="newCap" :used="activeWidgets" />
        <button v-else type="button" class="pb-button" @click="openCreate"><i class="mdi mdi-plus pb-ico" aria-hidden="true"></i>New widget</button>
      </template>
    </PageHeader>

    <SkeletonList v-if="loading" :rows="2" label="Loading widgets…" />
    <div v-else-if="error" class="pb-alert is-error" role="alert"><span>{{ error }}</span><button type="button" class="pb-button sm outline" @click="load">Retry</button></div>
    <EmptyState v-else-if="!widgets.length" icon="mdi-widgets-outline" title="Publish your first testimonial widget"
                text="Pick a layout; you can change it any time.">
      <button type="button" class="pb-button" @click="openCreate">Create widget</button>
    </EmptyState>

    <div v-else class="wv-list">
      <article v-for="widget in widgets" :key="widget.id" class="pb-card wv-item">
        <span :class="['lp-mini', widget.layout]" aria-hidden="true"><i class="mdi" :class="layoutIcon(widget.layout)"></i></span>
        <div class="wv-main">
          <div class="pb-row wv-top">
            <h2 class="wv-name"><router-link :to="'/widgets/' + widget.id">{{ widget.name }}</router-link></h2>
            <span :class="['pb-chip', widget.status === 'active' && !widget.refused ? 'approved' : 'is-warning']">{{ widget.refused ? 'Not showing' : (widget.status !== 'active' ? 'Paused' : (widget.viewed ? 'Live' : 'Active')) }}</span>
          </div>
          <p class="pb-muted">{{ layoutName(widget.layout) }} · {{ widget.selection.mode === 'picked' ? widget.selection.ids.length + ' hand-picked' : 'All published (' + widget.published + ')' }}</p>
          <p v-if="widget.refused" class="pb-help">{{ widget.refused }}</p>
        </div>
        <div class="pb-actions">
          <button type="button" class="pb-button sm outline" @click="copyCode(widget)"><i class="mdi mdi-code-tags pb-ico" aria-hidden="true"></i>Copy code</button>
          <router-link :to="'/widgets/' + widget.id" class="pb-button sm outline">Edit</router-link>
        </div>
      </article>
    </div>

    <v-dialog v-model="createOpen" max-width="640">
      <div class="pb-dialog">
        <div class="pb-dialog-head"><h2>Choose a layout</h2>
          <button type="button" class="pb-icon-button" aria-label="Close" @click="createOpen = false"><i class="mdi mdi-close" aria-hidden="true"></i></button></div>
        <p>You can switch layouts later without changing the code on your site.</p>
        <LayoutPicker :value="layout" :allowed="limits.layouts || ['grid']" @pick="pickLayout" />
        <div class="pb-field wv-name-field"><label class="pb-label" for="wv-new-name">Name <span class="optional">(only your team sees it)</span></label>
          <input id="wv-new-name" v-model="name" class="pb-input" maxlength="120" placeholder="Homepage widget"></div>
        <div v-if="savedTemplates.length" class="pb-field"><label class="pb-label" for="wv-template">Or start from a saved template</label>
          <select id="wv-template" v-model="templateId" class="pb-select"><option :value="null">No template</option>
            <option v-for="item in savedTemplates" :key="item.id" :value="item.id">{{ item.name }} · {{ item.summary }}</option></select></div>
        <div class="pb-dialog-actions">
          <button type="button" class="pb-button ghost" @click="createOpen = false">Cancel</button>
          <button type="button" class="pb-button" :disabled="busy" @click="create">{{ busy ? 'Creating…' : 'Create widget' }}</button>
        </div>
      </div>
    </v-dialog>
    <UpgradeDialog v-model="upgradeOpen" :detail="upgrade" :stacked="createOpen" />
  </div>
</template>

<script>
import EmptyState from '../components/EmptyState.vue';
import LayoutPicker, { LAYOUTS } from '../components/LayoutPicker.vue';
import LockedAction from '../components/LockedAction.vue';
import PageHeader from '../components/PageHeader.vue';
import SkeletonList from '../components/SkeletonList.vue';
import UpgradeDialog from '../components/UpgradeDialog.vue';
import { copyText } from '../components/SnippetBox.vue';
import workspace from '../mixins/workspace';
import { get, post, message, planLimit } from '../api';
import { toast } from '../lib/toast';
import { limitReached } from '../lib/upgrades';
import { embedSnippet } from '../publicUrls';

export default {
  name: 'WidgetsView',
  components: { EmptyState, LayoutPicker, LockedAction, PageHeader, SkeletonList, UpgradeDialog },
  mixins: [workspace],
  data: () => ({ widgets: [], loading: true, error: '', createOpen: false, layout: 'grid', name: '', templateId: null,
    savedTemplates: [], busy: false, upgrade: null, upgradeOpen: false }),
  computed: {
    // Plans with a widget limit have one brand, so its active widgets are the workspace's.
    activeWidgets() { return this.widgets.filter(item => item.status === 'active').length; },
    newCap() { return this.capOf('widgets', this.activeWidgets); }
  },
  watch: {
    brandId() { this.load(); },
    async createOpen(open) {
      if (open && this.limits.agency_templates && !this.savedTemplates.length) {
        try { this.savedTemplates = (await get('/templates', { kind: 'widget' })).templates || []; } catch (error) { this.savedTemplates = []; }
      }
    }
  },
  async mounted() {
    if (!(await this.requireWorkspace())) return;
    this.load();
  },
  methods: {
    layoutName(key) { const item = LAYOUTS.find(entry => entry.key === key); return item ? item.name : key; },
    layoutIcon(key) { return { grid: 'mdi-view-dashboard-outline', cards: 'mdi-view-carousel-outline', carousel: 'mdi-view-array-outline', quote: 'mdi-format-quote-open' }[key]; },
    async load() {
      if (!this.brandId) return;
      this.loading = true;
      this.error = '';
      try { this.widgets = (await get('/brands/' + this.brandId + '/widgets')).widgets || []; } catch (error) { this.error = message(error); } finally { this.loading = false; }
    },
    // At the plan's limit the upgrade note comes first (plans with a widget limit have one brand).
    openCreate() {
      const reached = limitReached('widgets', this.limits, this.activeWidgets, this.availableOn('widgets'));
      if (reached) { this.upgrade = reached; this.upgradeOpen = true; return; }
      this.createOpen = true;
    },
    // A locked layout opens the upgrade note over the picker: "Not now" returns to it with the layout unchanged.
    pickLayout(item) {
      if (item.locked) { this.upgrade = { feature: 'layouts' }; this.upgradeOpen = true; return; }
      this.layout = item.key;
    },
    async create() {
      this.busy = true;
      try {
        const body = { layout: this.layout, name: this.name.trim() || null };
        if (this.templateId) body.template_id = this.templateId;
        const widget = await post('/brands/' + this.brandId + '/widgets', body);
        this.createOpen = false;
        this.refreshWorkspace();
        this.$router.push({ path: '/widgets/' + widget.id, query: { created: '1' } }).catch(() => {});
      } catch (error) {
        const limit = planLimit(error);
        if (limit) { this.createOpen = false; this.upgrade = limit; this.upgradeOpen = true; } else toast(message(error), 'error');
      } finally { this.busy = false; }
    },
    async copyCode(widget) { await copyText(embedSnippet(widget, this.brand)); toast('Code copied. Paste it into your website.'); }
  }
};
</script>

<style scoped>
.wv-list{display:grid;gap:12px}
.wv-item{display:flex;align-items:center;gap:16px;padding:18px 20px}
.lp-mini{display:inline-grid;place-items:center;width:48px;height:48px;flex:0 0 48px;border-radius:12px;background:#F3EEE6;color:#963A22;font-size:24px}
.wv-main{flex:1;min-width:0}
.wv-top{flex-wrap:wrap;gap:10px;margin-bottom:4px}
.wv-name{margin:0;font:600 17px/1.3 var(--font-body)}
.wv-name a{color:#1F2328;text-decoration:none}
.wv-name a:hover{text-decoration:underline;text-underline-offset:3px}
.wv-name-field{margin-top:18px}
@media(max-width:700px){.wv-item{flex-wrap:wrap}.wv-item .pb-actions{width:100%}.wv-item .pb-actions .pb-button{flex:1}}
</style>
